import { createFilterOptions } from '@mui/material/Autocomplete'
import { Autocomplete, Box, TextField } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'es-toolkit/compat'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createEquipmentModel,
  type EquipmentFetchModel,
  fetchEquipmentModels,
} from 'api/equipment-model'

import type { EquipmentModelAPIRes } from 'api/equipment-model'
import { type FilterOptionsState } from '@mui/material'

declare module 'components/farm-settings/equipment/model-selector.js' {
  export type ModelSelectorProps = {
    makeBrandId: string | null
    equipmentType: string | null
    value: Partial<ModelOption> | null
    onValueChange: Function
    onLoad?: boolean
    disabled?: boolean
    id?: number | string
    property?: string
    apiRef?: any
    error?: boolean
    onBlur?: () => void
    testId?: string
  }
  type ModelOption = {
    id: string | null
    inputValue: string | undefined
    name: string
    makeBrandId: string | null
    equipmentType: string
  }
  export function ModelSelectorComponent(
    props: ModelSelectorProps,
  ): ReactElement<any, any>
}

export default function ModelSelectorComponent(
  props: ModelSelectorProps,
): ReactElement<any, any> {
  const { t } = useTranslation()
  const filter = createFilterOptions()

  const {
    makeBrandId,
    equipmentType,
    value,
    onValueChange,
    onLoad,
    disabled,
    id,
    property,
    apiRef,
    error,
    onBlur,
    testId,
  } = props

  const [isLoading, setIsLoading] = useState(true)
  const [models, setModels] = useState<Partial<ModelOption>[]>([])
  const [selectedVal, setSelectedVal] = useState<Partial<ModelOption | null>>({
    id: null,
    name: '',
  })

  const fetchModelsFromApi = useCallback(() => {
    setIsLoading(true)
    if (makeBrandId != null && equipmentType != null) {
      const data: EquipmentFetchModel = { makeBrandId, equipmentType }
      fetchEquipmentModels(data).then((apiModels: EquipmentModelAPIRes[]) => {
        const sortedModels: EquipmentModelAPIRes[] = [
          ...sortBy<EquipmentModelAPIRes>(
            apiModels.filter((m) => {
              const val = parseFloat(m.name)
              return !isNaN(val)
            }),
            (value) => parseFloat(value.name),
          ),
          ...sortBy(
            apiModels.filter((m) => {
              const val = parseFloat(m.name)
              return isNaN(val)
            }),
            ['name'],
          ),
        ]

        let setSelection = false
        const options: ModelOption[] = sortedModels.map((item) => {
          const newItem = {
            id: item.id,
            name: item.name,
            inputValue: '',
            makeBrandId: item.makeBrandId,
            equipmentType: item.equipmentType,
          }
          if (newItem.id == value?.id) {
            setSelection = true
            setSelectedVal(newItem)
          }
          return newItem
        })
        setModels(options)
        if (!setSelection) {
          const selection = options.find((option) => option.id === value?.id)
          setSelectedVal(selection || null)
        }

        setIsLoading(false)
      })
    } else {
      setModels([])
      setIsLoading(false)
      setSelectedVal(null)
    }
  }, [makeBrandId, equipmentType])

  useEffect(() => {
    fetchModelsFromApi()
    setSelectedVal(null)
  }, [makeBrandId, fetchModelsFromApi])

  const handleModelChange = useCallback(
    (e, newValue) => {
      const typedName =
        typeof newValue === 'string' ? newValue.trim() : newValue?.inputValue

      const existing = typedName
        ? models.find(
            (model) => model.name?.toLowerCase() === typedName.toLowerCase(),
          )
        : undefined

      if (existing) {
        setSelectedVal(existing)
        onValueChange(existing)
      } else if (typedName) {
        // Add a new model
        createEquipmentModel({
          makeBrandId,
          equipmentType,
          name: typedName,
        } as Partial<EquipmentModelAPIRes>).then((newModel) => {
          const newModelOption = {
            id: (newModel as EquipmentModelAPIRes).id,
            name: (newModel as EquipmentModelAPIRes).name,
            makeBrandId: (newModel as EquipmentModelAPIRes).makeBrandId,
            equipmentType: (newModel as EquipmentModelAPIRes).equipmentType,
            inputValue: '',
          }
          setModels([...models, newModelOption])
          setSelectedVal(newModelOption)
          onValueChange(newModelOption)
        })
      } else {
        setSelectedVal(newValue)
        onValueChange(newValue)
      }
    },
    [makeBrandId, models],
  )

  const renderName = useCallback((selectedVal) => {
    return selectedVal?.name
  }, [])

  return isLoading ? (
    <div>{t('loading')}</div>
  ) : !onLoad ? (
    <Autocomplete
      sx={{
        display: 'flex',
        width: '20ch',
        height: '100%',
        alignItems: 'center',
        '& .MuiFormControl-root': {
          my: 0,
          width: '100%',
        },
        '& .MuiAutocomplete-inputRoot': {
          alignItems: 'center',
          minHeight: 40,
        },
      }}
      disabled={disabled}
      key="model-selector"
      value={selectedVal}
      onBlur={onBlur}
      onChange={(e, newValue) => handleModelChange(e, newValue)}
      filterOptions={(
        options: Partial<ModelOption>[],
        params: FilterOptionsState<Partial<ModelOption>>,
      ) => {
        const filtered = filter(options, params)
        const { inputValue } = params

        const exists = models.find(
          (m) => m.name?.toLowerCase() === inputValue?.toLowerCase(),
        )

        if (inputValue !== '' && !exists) {
          filtered.push({
            inputValue,
            name: `${t('add')} "${inputValue}"`,
            id: filtered?.length,
          })
        }

        return filtered
      }}
      id="equipment-model-selector"
      options={models}
      getOptionLabel={(option: ModelOption) => {
        if (option.inputValue) {
          return option.inputValue
        }
        return option.name
      }}
      renderOption={(props, option: ModelOption) => (
        <li
          {...props}
          key={option.id}
          data-testid={
            testId &&
            `${testId}-option-${option.inputValue ? 'add' : option.id}`
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {option.inputValue !== '' ? (
              <Box>
                <FontAwesomeIcon icon={['fas', 'plus']} />
              </Box>
            ) : (
              ''
            )}
            <Box
              sx={{
                ml: 1,
              }}
            >
              {option.name}
            </Box>
          </Box>
        </li>
      )}
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          fullWidth
          onKeyDown={(event) => {
            if (
              event.key === 'Tab' &&
              id !== undefined &&
              property !== undefined
            ) {
              event.preventDefault()

              apiRef.current.stopCellEditMode({
                id,
                property,
              })

              apiRef.current.setCellFocus(id, property)
            }
          }}
          label={t('select_model')}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps?.htmlInput,
              'data-testid': testId,
            },
          }}
          size="small"
          variant="outlined"
          placeholder={t('select_model')}
        />
      )}
    />
  ) : (
    <>{renderName(selectedVal)}</>
  )
}
